import { useGet } from "@/lib/api";
import { LockedOverlay } from "@/components/LockedOverlay";
import { fmt } from "@/lib/format";
import { Zap, TrendingUp, TrendingDown, MoveHorizontal, AlertTriangle, Clock, Target, ShieldOff, Crosshair } from "lucide-react";

const DIR = {
  LONG: { color: "#10B981", bg: "rgba(16,185,129,0.15)", Icon: TrendingUp },
  SHORT: { color: "#EF4444", bg: "rgba(239,68,68,0.15)", Icon: TrendingDown },
  NONE: { color: "#9CA3AF", bg: "rgba(156,163,175,0.12)", Icon: MoveHorizontal },
};
const TREND = { UP: ["Uptrend", "#10B981", TrendingUp], DOWN: ["Downtrend", "#EF4444", TrendingDown], RANGE: ["Consolidation", "#9CA3AF", MoveHorizontal] };
const BIAS = { BUY: "#10B981", SELL: "#EF4444", NEUTRAL: "#9CA3AF" };

const t = (iso) => (iso ? new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : "—");
const mins = (iso) => Math.round((new Date(iso) - Date.now()) / 60000);
const human = (m) => (m < 60 ? `${m} min` : m < 1440 ? `${Math.round(m / 60)} h` : `${Math.round(m / 1440)} d`);

function Chip({ label, value, color = "#D1D5DB", testId }) {
  return (
    <div data-testid={testId} className="glass rounded-xl px-3 py-2 min-w-0">
      <div className="text-[9px] uppercase tracking-wider text-[#6B7280] font-mono-num">{label}</div>
      <div className="text-xs font-mono-num font-semibold leading-snug break-words" style={{ color }}>{value}</div>
    </div>
  );
}

function Level({ label, value, color, testId }) {
  return (
    <div data-testid={testId} className="glass rounded-2xl p-3 text-center">
      <div className="text-[9px] uppercase tracking-wider text-[#6B7280] font-mono-num mb-1">{label}</div>
      <div className="font-mono-num text-lg font-bold" style={{ color }}>{value}</div>
    </div>
  );
}

export function TechnicalCard({ refreshKey, tf = "15m", setTf }) {
  const { data: d, loading } = useGet(`/technical?tf=${tf}`, `${refreshKey}-${tf}`);
  if (loading && !d) return <section className="glass rounded-3xl p-6 text-center text-[#6B7280] font-mono-num text-sm">Scanning {tf} candles…</section>;
  if (d?.warming_up) return <section data-testid="technical-warming" className="glass rounded-3xl p-6 text-center text-[#F59E0B] font-mono-num text-sm animate-pulse">Warming up — first {tf} scan arrives within a minute or two…</section>;
  if (!d || d.error) return <section data-testid="technical-error" className="glass rounded-3xl p-5 text-sm text-[#9CA3AF]">{d?.error || "Intraday feed unavailable"}</section>;

  const sig = d.signal;
  const blackout = d.news?.blackout;
  const dir = sig ? sig.direction : "NONE";
  const { color, bg, Icon } = DIR[dir];
  const [trendLabel, trendColor, TrendIcon] = TREND[d.trend?.state || "RANGE"];
  const locked = d.locked;
  const nextNews = d.news?.next;

  return (
    <section data-testid="technical-card" className="glass rounded-3xl p-5 flex flex-col gap-4 relative overflow-hidden">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Zap size={16} className="text-[#F59E0B]" />
            <h2 className="font-display text-lg font-bold leading-none">Scalp Signal</h2>
          </div>
          <p className="text-[10px] text-[#6B7280] font-mono-num mt-1">W + D + H4 + H1 all aligned → {tf.toUpperCase()} BOS → EMA9 × EMA120 channel · SL 4× swing · TP 1.5× swing</p>
        </div>
        <div className="flex rounded-lg overflow-hidden border border-white/10 text-[11px] font-semibold shrink-0">
          {["15m", "5m"].map((x) => (
            <button key={x} data-testid={`tf-${x}`} onClick={() => setTf(x)}
              className={`px-2.5 py-1.5 ${tf === x ? "bg-[#F59E0B] text-black" : "bg-white/5 text-[#9CA3AF]"}`}>{x.toUpperCase()}</button>
          ))}
        </div>
      </div>

      {d.mtf && (
        <div data-testid="mtf-ladder" className={`rounded-2xl border p-3 flex flex-col gap-2 ${d.mtf.aligned ? (d.mtf.aligned === "UP" ? "border-[#10B981]/40 bg-[#10B981]/10" : "border-[#EF4444]/40 bg-[#EF4444]/10") : "border-white/10 bg-white/5"}`}>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider font-mono-num text-[#9CA3AF]">Higher-timeframe alignment</span>
            <span data-testid="mtf-aligned" className="text-xs font-semibold" style={{ color: d.mtf.aligned ? (d.mtf.aligned === "UP" ? "#10B981" : "#EF4444") : "#F59E0B" }}>
              {d.mtf.aligned ? (d.mtf.aligned === "UP" ? "ALL UP → buy-only scalps" : "ALL DOWN → sell-only scalps") : "NOT ALIGNED → no scalping"}
            </span>
          </div>
          <div className="grid grid-cols-4 gap-1.5">
            {[["weekly", "W"], ["daily", "D"], ["h4", "H4"], ["h1", "H1"]].map(([k, l]) => {
              const st = d.mtf[k]?.state || "RANGE";
              const c = st === "UP" ? "#10B981" : st === "DOWN" ? "#EF4444" : "#9CA3AF";
              return (
                <div key={k} data-testid={`mtf-${k}`} title={d.mtf[k]?.detail} className="rounded-lg bg-black/20 px-2 py-1.5 text-center">
                  <div className="text-[9px] font-mono-num text-[#6B7280]">{l}</div>
                  <div className="text-xs font-bold font-mono-num" style={{ color: c }}>{st === "UP" ? "▲ UP" : st === "DOWN" ? "▼ DOWN" : "— RANGE"}</div>
                </div>
              );
            })}
          </div>
          <p className="text-[10px] text-[#9CA3AF] leading-relaxed">{d.mtf.summary}</p>
        </div>
      )}

      {blackout ? (
        <div data-testid="news-blackout" className="rounded-2xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-4 flex flex-col gap-3">
          <div className="flex items-center gap-2 text-[#F59E0B] font-semibold text-sm">
            <ShieldOff size={16} /> Scalping paused — news window
          </div>
          <div className="text-xs text-[#D1D5DB]">
            <span className="font-semibold">{d.news.event.title}</span>{" "}
            {d.news.event.minutes >= 0 ? `releases in ${d.news.event.minutes} min` : `released ${-d.news.event.minutes} min ago · cool-off`}
            {" "}· EMA9 crosses are ignored from {d.news.before_min} min before until {d.news.cooloff_min} min after. Master Signal alerts go to your bell {d.news.before_min} min before and {d.news.after_min} min after this release.
          </div>
          {d.pre_news && (
            <div data-testid="pre-news-bias" className="relative rounded-xl bg-[#0A0D14]/60 p-3 overflow-hidden">
              <div className="text-[9px] uppercase tracking-wider text-[#6B7280] font-mono-num mb-1">Pre-news XAU bias · from recent key data</div>
              <div className="flex items-center gap-2">
                <span className="font-display text-2xl font-extrabold" style={{ color: BIAS[d.pre_news.bias] }}>{d.pre_news.bias}</span>
                <span className="text-xs text-[#9CA3AF] font-mono-num">{d.pre_news.confidence}% · overall read: {d.pre_news.macro_action} · Fed: {d.pre_news.fed.toLowerCase()}</span>
              </div>
              {d.pre_news.reason && <p className="text-xs text-[#D1D5DB] mt-1.5 leading-relaxed">{d.pre_news.reason}</p>}
              {locked && <LockedOverlay label="Unlock pre-news analysis" />}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-col items-center text-center gap-2 py-1">
          {d.research?.mode && (
            <div data-testid="research-mode-banner" className="w-full text-left rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-3 py-2 text-[11px] leading-relaxed text-[#FCD34D]">
              <span className="font-semibold uppercase tracking-wider text-[10px]">Research mode</span> — no stop/target rule has shown a positive Net $/oz over {d.research.min_trades}+ closed trades yet, so these alerts are for study, not for trading.
              {d.research.best && <> Best so far on {tf.toUpperCase()}: {d.research.best.label} → {d.research.best.net_usd >= 0 ? "+" : ""}{d.research.best.net_usd} $/oz on {d.research.best.closed} trades.</>}
            </div>
          )}
          <div data-testid="tech-direction" className="flex items-center gap-2 px-5 py-2 rounded-full font-display font-extrabold text-2xl"
            style={{ color, background: bg, border: `1px solid ${color}55`, opacity: sig && !sig.fresh ? 0.55 : 1 }}>
            <Icon size={22} /> {sig ? sig.direction : "NO TRADE"}
            {sig && <span className="text-sm font-mono-num font-semibold opacity-80">SL {sig.sl_mult ?? 1}× · TP {sig.tp_mult ?? 3}×</span>}
            {sig && !sig.fresh && <span data-testid="tech-last-signal-tag" className="text-[10px] font-mono-num font-semibold uppercase tracking-wider opacity-90 ml-1">last signal</span>}
          </div>
          {sig ? (
            <div className="text-[11px] text-[#9CA3AF] font-mono-num flex items-center gap-1.5">
              <Clock size={11} /> {sig.sequence === "cross after BOS" ? `cross at ${t(sig.cross_time)} after BOS` : `cross at ${t(sig.cross_time)}, BOS confirmed ${t(sig.time)}`} · {sig.fresh ? "live setup" : `${sig.bars_since} bars ago`} · BOS {fmt(sig.bos_level)}
            </div>
          ) : (
            <p data-testid="tech-reason" className="text-xs text-[#9CA3AF] max-w-md leading-relaxed">{d.reason}</p>
          )}
          {sig && !sig.fresh && <p data-testid="tech-reason" className="text-[11px] text-[#F59E0B]/90 max-w-md">{d.reason}</p>}
        </div>
      )}

      {sig && !blackout && (
        <div className="relative grid grid-cols-3 gap-3 rounded-2xl overflow-hidden">
          <Level label="Entry" value={locked ? "$••••" : `$${fmt(sig.entry)}`} color="#F59E0B" testId="tech-entry" />
          <Level label="Stop Loss (1R)" value={locked ? "$••••" : `$${fmt(sig.stop_loss)}`} color="#EF4444" testId="tech-sl" />
          <Level label="Take Profit (3R)" value={locked ? "$••••" : `$${fmt(sig.take_profit)}`} color="#10B981" testId="tech-tp" />
          {locked && <LockedOverlay label="Unlock Entry / SL / TP" />}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <Chip label="H1 trend" value={<span className="flex items-center gap-1"><TrendIcon size={12} />{trendLabel}</span>} color={trendColor} testId="tech-trend" />
        <Chip label={`EMA9 vs EMA120 channel (${tf})`} value={<>{fmt(d.ema9)} <span className="uppercase">{d.ema9_vs_channel}</span><br />{fmt(d.ema120_low)}–{fmt(d.ema120_high)}</>}
          color={d.ema9_vs_channel === "above" ? "#10B981" : d.ema9_vs_channel === "below" ? "#EF4444" : "#9CA3AF"} testId="tech-ema-channel" />  <Chip label="Last BOS" value={d.bos ? `${d.bos.direction === "UP" ? "↑" : "↓"} ${fmt(d.bos.level)} @ ${t(d.bos.time)}` : "none"} testId="tech-bos" />
        <Chip label="Next high-impact" value={nextNews ? `${nextNews.title.split(" (")[0]} · ${human(Math.max(0, mins(nextNews.release_at)))}` : "—"} color="#F59E0B" testId="tech-next-news" />
      </div>

      {!locked && d.stats && (
        <div data-testid="tech-stats" className="flex items-center justify-between text-[11px] font-mono-num text-[#6B7280] border-t border-white/5 pt-3">
          <span className="flex items-center gap-1"><Crosshair size={11} /> {d.stats.signals} signals · {d.stats.wins}W / {d.stats.losses}L{d.stats.win_rate != null ? ` · ${d.stats.win_rate}%` : ""}</span>
          <span className="flex items-center gap-1"><Target size={11} /> net {d.stats.net_r >= 0 ? "+" : ""}{d.stats.net_r}R · as of {t(d.as_of)}</span>
        </div>
      )}
      <p className="text-[10px] text-[#4B5563] flex items-start gap-1 leading-relaxed">
        <AlertTriangle size={11} className="mt-0.5 shrink-0" /> Rules-based, not advice. Each timeframe's trend = direction of its latest break of structure; scalps only when Weekly, Daily, H4 and H1 all agree — one signal per EMA9 × EMA120-channel cross, only after a {tf.toUpperCase()} break of structure in that direction.
        {d.feed && <span data-testid="tech-feed" style={{ color: d.feed.live ? "#10B981" : "#F59E0B" }}> Candles: {d.feed.live ? `live spot${d.feed.stale ? " · market quiet/closed" : ""}, last closed bar ${t(d.feed.quote_time)}` : "exchange-delayed ~10 min"} · refreshes every minute.</span>}
      </p>
    </section>
  );
}
